import React, { useMemo } from 'react';
import { Chip } from '@heroui/react';
import { Warning } from '@phosphor-icons/react';
import { useMassMailContext } from '../context/MassMailContext';
import { CcBccSearch, CcBccContact } from './CcBccSearch';
import { hasUsableEmail, resolveRecipientTypes } from '../utils/recipientTypes';
import { planRecipientChange } from '../utils/recipientSync';
import { Contact, RecipientType } from '../types';

/**
 * Toont bij de mail zelf wie er in Aan, CC en BCC staat, en laat CC/BCC hier
 * aanvullen. Werkt rechtstreeks op `state.contacts` — dezelfde lijst als de tab
 * "Contactpersonen" — zodat beide kanten altijd hetzelfde tonen: wie je hier
 * toevoegt verschijnt links in de contactenlijst en omgekeerd.
 *
 * Alleen voor flows met `enableRecipientTypes` (één gezamenlijke mail). De gewone
 * mass mail houdt zijn losse `ccContacts`/`bccContacts`, want daar krijgt elk
 * contact een eigen mail en bestaat er geen gedeeld ontvangersveld.
 */
const toCcBccContact = (contact: Contact): CcBccContact => ({
  name: contact.name || contact.email || '',
  email: (contact.email || '').trim(),
});

const labelVoor = (contact: Contact): string =>
  contact.name && contact.name !== contact.email
    ? `${contact.name} <${contact.email}>`
    : contact.email || '';

export const RecipientFields: React.FC = () => {
  const { state, dispatch } = useMassMailContext();

  // Zelfde afleiding als de contactenlijst: "Aan" hier betekent "Aan" daar.
  const rollen = useMemo(() => resolveRecipientTypes(state.contacts), [state.contacts]);

  const perRol = (rol: RecipientType) =>
    state.contacts.filter((contact, index) => rollen[index] === rol && hasUsableEmail(contact));

  const toContacten = perRol('to');
  const ccContacten = perRol('cc');
  const bccContacten = perRol('bcc');

  const handleChange = (
    recipientType: RecipientType,
    huidige: Contact[],
    nieuweLijst: CcBccContact[],
  ) => {
    planRecipientChange({
      recipientType,
      huidige,
      nieuweLijst,
      contacts: state.contacts,
      excludedContacts: state.excludedContacts,
    }).forEach((actie) => {
      switch (actie.type) {
        case 'set-role':
          dispatch({
            type: 'SET_CONTACT_RECIPIENT_TYPE',
            payload: { contactId: actie.contactId, recipientType: actie.recipientType },
          });
          break;
        case 'restore':
          dispatch({ type: 'RESTORE_CONTACT', payload: actie.contact });
          dispatch({
            type: 'SET_CONTACT_RECIPIENT_TYPE',
            payload: { contactId: actie.contact.id, recipientType: actie.recipientType },
          });
          break;
        case 'add':
          dispatch({ type: 'SET_CONTACTS', payload: [...state.contacts, ...actie.contacts] });
          break;
        case 'remove':
          dispatch({ type: 'REMOVE_CONTACT', payload: actie.contactId });
          break;
      }
    });
  };

  return (
    <div className="space-y-3">
      {/* Aan — de rol zelf wijzig je bij de contactpersonen */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Aan</label>
        {toContacten.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {toContacten.map((contact) => (
              <Chip key={contact.id} size="sm" variant="flat" color="primary">
                <span className="text-xs">{labelVoor(contact)}</span>
              </Chip>
            ))}
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5">
            <Warning size={14} weight="fill" className="mt-0.5 shrink-0 text-blue-600" />
            <span className="text-xs text-blue-800">
              Niemand in &ldquo;Aan&rdquo; — de mail vertrekt met alleen CC- en BCC-ontvangers. Zet iemand op
              &ldquo;Aan&rdquo; bij de contactpersonen als er wél een zichtbare geadresseerde moet zijn.
            </span>
          </div>
        )}
      </div>

      <CcBccSearch
        label="CC"
        contacts={ccContacten.map(toCcBccContact)}
        onChange={(nieuweLijst) => handleChange('cc', ccContacten, nieuweLijst)}
      />
      <CcBccSearch
        label="BCC"
        contacts={bccContacten.map(toCcBccContact)}
        onChange={(nieuweLijst) => handleChange('bcc', bccContacten, nieuweLijst)}
      />
    </div>
  );
};
